<template>
  <div class="data-report-container">
    <ds-page-header
      :title="$t('dataReport.pageTitle')"
      :subtitle="$t('dataReport.pageSubtitle')">
    </ds-page-header>

    <ds-search-panel>
      <el-row :gutter="16" class="data-report-filters">
        <el-col :xs="24" :sm="12" :md="8" v-if="dataReportInfo.communitys.length > 1">
          <el-select v-model="dataReportInfo.conditions.communityId" :placeholder="$t('dataReport.selectCommunity')"
            style="width: 100%" @change="onCommunityInput">
            <el-option v-for="(item, index) in dataReportInfo.communitys" :key="index" :label="item.name"
              :value="item.communityId" />
          </el-select>
        </el-col>
        <el-col :xs="24" :sm="12" :md="10">
          <span class="data-report-filter-label">{{ $t('dataReport.timeRange') }}：</span>
          <el-date-picker v-model="dataReportInfo.conditions.startDate" type="date" value-format="yyyy-MM-dd"
            :placeholder="$t('dataReport.startDate')" class="data-report-date" @change="onDateInput" />
          <span class="data-report-filter-label">{{ $t('dataReport.to') }}</span>
          <el-date-picker v-model="dataReportInfo.conditions.endDate" type="date" value-format="yyyy-MM-dd"
            :placeholder="$t('dataReport.endDate')" class="data-report-date" @change="onDateInput" />
        </el-col>
      </el-row>
      <template slot="actions">
        <el-button type="primary" size="small" icon="el-icon-search" @click="_doSearch">{{ $t('dataReport.search') }}</el-button>
        <el-button-group class="data-report-quick">
          <el-button size="small" :type="dataReportInfo.curDay === 'today' ? 'primary' : ''" @click="_changeDate('today')">{{
            $t('dataReport.today') }}</el-button>
          <el-button size="small" :type="dataReportInfo.curDay === 'yesterday' ? 'primary' : ''" @click="_changeDate('yesterday')">{{
            $t('dataReport.yesterday') }}</el-button>
          <el-button size="small" :type="dataReportInfo.curDay === 'seven' ? 'primary' : ''" @click="_changeDate('seven')">{{
            $t('dataReport.last7Days') }}</el-button>
          <el-button size="small" :type="dataReportInfo.curDay === 'thirty' ? 'primary' : ''" @click="_changeDate('thirty')">{{
            $t('dataReport.last30Days') }}</el-button>
        </el-button-group>
      </template>
    </ds-search-panel>

    <!-- ③ 核心 KPI 区：6 张头号指标，其余收进折叠区 -->
    <div class="data-report-scope">{{ scopeLabel }}</div>
    <el-row :gutter="16" class="data-report-headline">
      <el-col :xs="24" :sm="12" :md="8" :lg="4" v-for="kpi in headlineKpis" :key="kpi.key">
        <ds-kpi-card
          :label="kpi.label"
          :value="kpi.display"
          :tone="kpi.tone">
          <template slot="footer">{{ kpi.desc }}</template>
        </ds-kpi-card>
      </el-col>
    </el-row>

    <el-collapse class="data-report-more">
      <el-collapse-item :title="$t('dataReport.moreTitle', { count: otherCount })" name="more">
        <div class="data-report-kpi-row">
          <el-card class="data-report-item" v-for="group in kpiGroups" :key="group.key" shadow="never">
            <div class="data-report-title">{{ group.title }}</div>
            <div class="data-report-grid">
              <div v-for="(item, index) in group.items" :key="index" class="data-report-card">
                <div class="data-report-card-title">{{ item.name }}</div>
                <div class="data-report-card-value">{{ formatKpiValue(item) }}</div>
              </div>
            </div>
          </el-card>
        </div>
      </el-collapse-item>
    </el-collapse>

    <!-- ④ 趋势与构成区：全量接口数据，不新增接口 -->
    <data-report-overview-charts ref="overviewCharts" />

    <el-card class="data-report-tabs">
      <el-tabs v-model="dataReportInfo._currentTab" @tab-click="changeTab(dataReportInfo._currentTab)">
          <el-tab-pane :label="$t('dataReport.tab.earnedStatistics')" name="dataReportEarnedStatistics" />
          <el-tab-pane :label="$t('dataReport.tab.earnedDetail')" name="dataReportEarnedDetailStatistics" />
          <el-tab-pane :label="$t('dataReport.tab.earnedWay')" name="dataReportEarnedWayStatistics" />
          <el-tab-pane :label="$t('dataReport.tab.oweStatistics')" name="dataReportOweStatistics" />
          <el-tab-pane :label="$t('dataReport.tab.oweDetail')" name="dataReportOweDetailStatistics" />
          <el-tab-pane :label="$t('dataReport.tab.feeStatistics')" name="dataReportFeeStatistics" />
          <el-tab-pane :label="$t('dataReport.tab.monthReceived')" name="dataMonthReceivedStatistics" />
          <el-tab-pane :label="$t('dataReport.tab.monthOwe')" name="dataMonthOweStatistics" />
        </el-tabs>

        <component :is="dataReportInfo._currentTab" :ref="dataReportInfo._currentTab"/>
      </el-card>
    </div>
</template>

<script>
import { getCommunityId } from '@/api/community/communityApi'
import {
  queryFeeDataReport,
  queryOrderDataReport,
  queryInoutDataReport,
  queryOthersDataReport,
  listMyEnteredCommunitys
} from '@/api/report/dataReportApi'
import DataReportEarnedStatistics from '@/components/report/DataReportEarnedStatistics'
import DataReportEarnedDetailStatistics from '@/components/report/DataReportEarnedDetailStatistics'
import DataReportEarnedWayStatistics from '@/components/report/DataReportEarnedWayStatistics'
import DataReportOweStatistics from '@/components/report/DataReportOweStatistics'
import DataReportOweDetailStatistics from '@/components/report/DataReportOweDetailStatistics'
import DataReportFeeStatistics from '@/components/report/DataReportFeeStatistics'
import DataMonthReceivedStatistics from '@/components/report/DataMonthReceivedStatistics'
import DataMonthOweStatistics from '@/components/report/DataMonthOweStatistics'
import DsPageHeader from '@/components/ds/ds-page-header.vue'
import DsSearchPanel from '@/components/ds/ds-search-panel.vue'
import DsKpiCard from '@/components/ds/ds-kpi-card.vue'
import DataReportOverviewCharts from '@/components/report/DataReportOverviewCharts.vue'
import { formatMoney, formatNumber } from '@/utils/format'
import { dateFormat } from '@/utils/dateUtil'

export default {
  name: 'DataReportList',
  components: {
    DsPageHeader,
    DsSearchPanel,
    DsKpiCard,
    DataReportOverviewCharts,
    DataReportEarnedStatistics,
    DataReportEarnedDetailStatistics,
    DataReportEarnedWayStatistics,
    DataReportOweStatistics,
    DataReportOweDetailStatistics,
    DataReportFeeStatistics,
    DataMonthReceivedStatistics,
    DataMonthOweStatistics
  },
  data() {
    return {
      dataReportInfo: {
        curDay: '',
        _currentTab: 'dataReportEarnedStatistics',
        fees: [],
        orders: [],
        inouts: [],
        others: [],
        communitys: [],
        conditions: {
          startDate: '',
          endDate: '',
          communityId: ''
        }
      }
    }
  },
  computed: {
    /**
     * KPI 分组（UI-REPORT-002）
     * 仅做展示层分组，接口与字段全部沿用原 queryFeeDataReport 等返回结构，
     * **不新增任何接口、不改变任何业务计算口径**。
     */
    kpiGroups() {
      return [
        {
          key: 'fee',
          title: this.$t('dataReport.feeStatistics'),
          items: this.dataReportInfo.fees || []
        },
        {
          key: 'order',
          title: this.$t('dataReport.orderStatistics'),
          items: this.dataReportInfo.orders || []
        },
        {
          key: 'inout',
          title: this.$t('dataReport.inoutStatistics'),
          items: this.dataReportInfo.inouts || []
        },
        {
          key: 'other',
          title: this.$t('dataReport.otherStatistics'),
          items: this.dataReportInfo.others || []
        }
      ]
    },
    /**
     * 核心 6 张 KPI：全部取自费用类全量接口，不做任何计算。
     * 后端 name 为中文硬编码，这里按名精确匹配，匹配不到则顺位兜底，
     * 数值与分组区完全一致，仅展示形态变化（千分位/货币符号）。
     */
    headlineKpis() {
      const _fees = this.dataReportInfo.fees || []
      const _find = (_name) => _fees.find(item => item.name === _name)
      const _defs = [
        { key: 'received', name: '实收金额', tone: 'primary', money: true },
        { key: 'owe', name: '欠费金额', tone: 'danger', money: true },
        { key: 'discount', name: '优惠金额', tone: 'info', money: true },
        { key: 'late', name: '滞纳金', tone: 'warning', money: true },
        { key: 'prestore', name: '账户预存', tone: 'success', money: true },
        { key: 'withhold', name: '账户扣款', tone: 'muted', money: true }
      ]
      return _defs.map(def => {
        const _hit = _find(def.name)
        const _raw = _hit ? _hit.value : ''
        return {
          key: def.key,
          label: def.name,
          tone: def.tone,
          display: def.money ? formatMoney(_raw) : formatNumber(_raw),
          desc: this.$t('dataReport.headlineDesc')
        }
      })
    },
    otherCount() {
      const _all = (this.dataReportInfo.fees || []).length
        + (this.dataReportInfo.orders || []).length
        + (this.dataReportInfo.inouts || []).length
        + (this.dataReportInfo.others || []).length
      return Math.max(_all - 6, 0)
    },
    scopeLabel() {
      return this.$t('dataReport.scopeLabel', {
        start: this.dataReportInfo.conditions.startDate || '—',
        end: this.dataReportInfo.conditions.endDate || '—'
      })
    }
  },
  created() {
    this._initDate()
    this.dataReportInfo.conditions.communityId = getCommunityId()
    this._loadStaffCommunitys()
    this._loadDataReportFee()
    this._loadDataReportOrder()
    this._loadDataReportInout()
    this._loadDataReportOthers()
  },
  mounted() {
    // 首屏同时打开明细 Tab 与概览图表，保持原有 open(conditions) 调用链
    this.changeTab(this.dataReportInfo._currentTab)
    this._refreshOverview()
  },
  methods: {
    _initDate() {
      // 默认展示近 30 天数据，但快捷按钮默认不选中，由用户主动选择区间后点击搜索
      const _endDate = new Date()
      const _startDate = new Date()
      _startDate.setDate(_startDate.getDate() - 30)
      this.dataReportInfo.conditions.startDate = dateFormat(_startDate)
      this.dataReportInfo.conditions.endDate = dateFormat(_endDate)
      this.dataReportInfo.curDay = ''
    },
    onDateInput() {
      // 手动改日期仅清空快捷选中态，不自动查询，等点击搜索按钮
      this.dataReportInfo.curDay = ''
    },
    onCommunityInput() {
      // 切换小区不同步清空快捷选中，仅更新条件，等点击搜索按钮
    },
    _doSearch() {
      this._loadDataReportFee()
      this._loadDataReportOrder()
      this._loadDataReportInout()
      this._loadDataReportOthers()
      this.changeTab(this.dataReportInfo._currentTab)
      this._refreshOverview()
    },
    _changeDate(_day) {
      // 快捷区间：填充日期 + 高亮，并立即查询
      if (_day && !['today', 'yesterday', 'seven', 'thirty'].includes(_day)) {
        return
      }
      this.dataReportInfo.curDay = _day
      const _endDate = new Date()
      if (_day === 'today') {
        this.dataReportInfo.conditions.endDate = dateFormat(_endDate)
        this.dataReportInfo.conditions.startDate = dateFormat(_endDate)
      } else if (_day === 'yesterday') {
        _endDate.setDate(_endDate.getDate() - 1)
        this.dataReportInfo.conditions.endDate = dateFormat(_endDate)
        this.dataReportInfo.conditions.startDate = dateFormat(_endDate)
      } else if (_day === 'seven') {
        this.dataReportInfo.conditions.endDate = dateFormat(_endDate)
        _endDate.setDate(_endDate.getDate() - 7)
        this.dataReportInfo.conditions.startDate = dateFormat(_endDate)
      } else if (_day === 'thirty') {
        this.dataReportInfo.conditions.endDate = dateFormat(_endDate)
        _endDate.setDate(_endDate.getDate() - 30)
        this.dataReportInfo.conditions.startDate = dateFormat(_endDate)
      }
      this._loadDataReportFee()
      this._loadDataReportOrder()
      this._loadDataReportInout()
      this._loadDataReportOthers()
      this.changeTab(this.dataReportInfo._currentTab)
      this._refreshOverview()
    },
    formatKpiValue(item) {
      if (!item) return '—'
      const _name = item.name || ''
      const _moneyKeys = ['金额', '实收', '欠费', '优惠', '滞纳', '预存', '扣款', '收入', '退款', '充电', '押金', '采购']
      const _isMoney = _moneyKeys.some(k => _name.includes(k))
      if (_isMoney) return formatMoney(item.value)
      const _num = parseFloat(item.value)
      if (isNaN(_num)) return item.value
      return formatNumber(_num, 0)
    },
    _refreshOverview() {
      this.$nextTick(() => {
        const _overview = this.$refs.overviewCharts
        if (_overview && typeof _overview.open === 'function') {
          _overview.open(this.dataReportInfo.conditions)
        }
      })
    },
    async _loadDataReportFee() {
      try {
        const { data } = await queryFeeDataReport(this.dataReportInfo.conditions)
        this.dataReportInfo.fees = data
      } catch (error) {
        console.error('Failed to load fee data:', error)
      }
    },
    async _loadDataReportOrder() {
      try {
        const { data } = await queryOrderDataReport(this.dataReportInfo.conditions)
        this.dataReportInfo.orders = data
      } catch (error) {
        console.error('Failed to load order data:', error)
      }
    },
    async _loadDataReportInout() {
      try {
        const { data } = await queryInoutDataReport(this.dataReportInfo.conditions)
        this.dataReportInfo.inouts = data
      } catch (error) {
        console.error('Failed to load inout data:', error)
      }
    },
    async _loadDataReportOthers() {
      try {
        const { data } = await queryOthersDataReport(this.dataReportInfo.conditions)
        this.dataReportInfo.others = data
      } catch (error) {
        console.error('Failed to load other data:', error)
      }
    },
    changeTab(_tab) {
      this.dataReportInfo._currentTab = _tab
      // 原实现使用 setTimeout(500) 等待动态组件挂载，存在竞态：
      // 快速切换 Tab 时可能拿到 undefined 的 ref，或对已销毁组件调用 open。
      // 改为 $nextTick + 实例有效性检查。
      // 见 docs/UI-REFACTOR-SPEC.md UI-REPORT-001
      this.$nextTick(() => {
        const _instance = this.$refs[_tab]
        if (_instance && typeof _instance.open === 'function') {
          _instance.open(this.dataReportInfo.conditions)
        }
      })
    },
    async _loadStaffCommunitys() {
      try {
        const { communitys } = await listMyEnteredCommunitys({
          _uid: '123mlkdinkldldijdhuudjdjkkd',
          page: 1,
          row: 100
        })
        this.dataReportInfo.communitys = communitys
      } catch (error) {
        console.error('Failed to load communities:', error)
      }
    },
    _changCommunity() {
      this.changeTab(this.dataReportInfo._currentTab)
      this._loadDataReportFee()
      this._loadDataReportOrder()
      this._loadDataReportInout()
      this._loadDataReportOthers()
      this._refreshOverview()
    }
  }
}
</script>

<style lang="scss" scoped>
.data-report-container {
  padding: var(--ds-spacing-lg);
  background: var(--ds-gray-100);
  min-height: 100%;

  .data-report-filters {
    align-items: center;
  }

  .data-report-filter-label {
    font-size: var(--ds-font-size-sm);
    color: var(--ds-gray-600);
    margin: 0 8px;
    white-space: nowrap;

    &:first-child {
      margin-left: 0;
    }
  }

  .data-report-date {
    width: 150px;
  }

  .data-report-quick {
    flex-shrink: 0;
  }

  .data-report-scope {
    font-size: var(--ds-font-size-xs);
    color: var(--ds-gray-500);
    margin: var(--ds-spacing-sm) 0;
  }

  .data-report-headline {
    margin-bottom: var(--ds-spacing-sm);

    .el-col {
      margin-bottom: var(--ds-spacing-md);
    }
  }

  .data-report-more {
    margin-bottom: var(--ds-spacing-md);
    border: 1px solid var(--ds-gray-300);
    border-radius: var(--ds-radius-md);
    background: #fff;
    padding: 0 var(--ds-spacing-lg);
  }

  .data-report-tabs {
    margin-top: var(--ds-spacing-md);
  }

  /* UI-REPORT-002 修正：KPI 四个分组改为 Grid 自适应列。
     原为 flex + flex:1 1 0 + nowrap，在窄视口下每组仅剩 ~125px，
     组内 12~15 个小卡纵向堆叠，整块高度撑到 1200px+，把下方 Tab 挤出首屏。
     改为 Grid 后：分组按可用宽度自动换行，组内小卡按 minmax 自动分列。
     外层再加横向滚动，保证窄视口下也能横向对比四组数据。 */
  .data-report-kpi-row {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: var(--ds-spacing-md);
    margin-bottom: var(--ds-spacing-md);
  }

  @media (min-width: 1280px) {
    .data-report-kpi-row {
      grid-template-columns: repeat(4, minmax(0, 1fr));
    }
  }

  .data-report-item {
    min-width: 0;
    margin-bottom: 0;

    .data-report-title {
      font-weight: bold;
      margin-bottom: 15px;
      font-size: var(--ds-font-size-lg);
      color: var(--ds-gray-800);
    }

    .data-report-grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(112px, 1fr));
      gap: var(--ds-spacing-sm);
    }

    .data-report-card {
      min-width: 0;
      padding: 10px;
      border: 1px solid var(--ds-gray-300);
      border-radius: var(--ds-radius-md);
      background: #fff;
      transition: border-color var(--ds-transition-fast),
                  box-shadow var(--ds-transition-fast);

      &:hover {
        border-color: rgba(var(--ds-primary-rgb), 0.4);
        box-shadow: var(--ds-shadow-sm);
      }

      .data-report-card-title {
        font-size: var(--ds-font-size-xs);
        color: var(--ds-gray-500);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .data-report-card-value {
        font-size: var(--ds-font-size-lg);
        font-weight: bold;
        margin-top: 5px;
        color: var(--ds-gray-900);
        font-family: var(--ds-font-family-num);
        font-variant-numeric: tabular-nums;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
    }
  }

  .margin-bottom {
    margin-bottom: 20px;
  }

  .padding {
    padding: 10px 0;
  }

  .flex {
    display: flex;
  }

  .justify-between {
    justify-content: space-between;
  }

  .justify-start {
    justify-content: flex-start;
  }

  .flex-wrap {
    flex-wrap: wrap;
  }
}
</style>